import { FilterList } from "@mui/icons-material";
import { Badge, Button, Menu, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useState } from "react";
import { TaskItemsFilterToggleButton, TaskItemsSortToggleButton } from "../../../../components";
import {
  SortByDirection,
  TaskItemsFilterByOption,
  TaskItemsSortByOption,
  TaskItemStatus,
  TeamTaskItemsGroupByOption,
} from "../../../../constants";
import { useTeamImplementationSettings } from "../hooks";

interface Props {
  teamSlug: string;
  disabled?: boolean;
}

const TeamTaskItemsFilterButton = (props: Props) => {
  const { settings, updateSettings } = useTeamImplementationSettings(props.teamSlug);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  function handleGroupByChanged(value: TeamTaskItemsGroupByOption) {
    if (value != null) {
      updateSettings({
        taskItemsGroupByOption: value,
      });
    }
  }

  function handleFilterByChanged(
    filterByOption: TaskItemsFilterByOption,
    filterByStatusOptions: TaskItemStatus[] | null,
  ) {
    updateSettings({
      taskItemsFilterByOption: filterByOption,
      taskItemsFilterByStatusOptions: filterByStatusOptions,
    });
  }

  function handleSortByChanged(value: TaskItemsSortByOption) {
    if (value != null) {
      updateSettings({
        taskItemsSortByOption: value,
      });
    }
  }

  function handleSortDirectionChanged() {
    if (settings.taskItemsSortByDirection === SortByDirection.Ascending) {
      updateSettings({
        taskItemsSortByDirection: SortByDirection.Descending,
      });
    } else {
      updateSettings({
        taskItemsSortByDirection: SortByDirection.Ascending,
      });
    }
  }

  return (
    <>
      <Badge color="primary" variant="dot" invisible={settings.taskItemsFilterByOption === TaskItemsFilterByOption.All}>
        <Button
          variant="tertiary"
          disabled={props.disabled}
          sx={{ minWidth: 0, p: 0.25 }}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <FilterList />
        </Button>
      </Badge>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
      >
        <Typography variant="overline" sx={{ mx: 0.5 }}>
          Group By
        </Typography>
        <ToggleButtonGroup
          value={settings.taskItemsGroupByOption}
          exclusive
          orientation="vertical"
          variant="menu"
          onChange={(_, value) => handleGroupByChanged(value)}
          sx={{ minWidth: "100%" }}
        >
          <ToggleButton value={TeamTaskItemsGroupByOption.None}>
            <Typography>None</Typography>
          </ToggleButton>
          <ToggleButton value={TeamTaskItemsGroupByOption.DueDate}>
            <Typography>Due Date</Typography>
          </ToggleButton>
          <ToggleButton value={TeamTaskItemsGroupByOption.InFocus}>
            <Typography>In Focus</Typography>
          </ToggleButton>
          <ToggleButton value={TeamTaskItemsGroupByOption.Owner}>
            <Typography>Owner</Typography>
          </ToggleButton>
          <ToggleButton value={TeamTaskItemsGroupByOption.PrimaryOwner}>
            <Typography>Primary Owner</Typography>
          </ToggleButton>
          <ToggleButton value={TeamTaskItemsGroupByOption.Status}>
            <Typography>Status</Typography>
          </ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="overline" sx={{ mx: 0.5, mt: 1 }}>
          Filter By
        </Typography>
        <TaskItemsFilterToggleButton
          filterByOption={settings.taskItemsFilterByOption}
          filterByStatusOptions={settings.taskItemsFilterByStatusOptions}
          onChange={handleFilterByChanged}
        />

        <Typography variant="overline" sx={{ mx: 0.5, mt: 1 }}>
          Sort By
        </Typography>
        <TaskItemsSortToggleButton
          sortByOption={settings.taskItemsSortByOption}
          sortByDirection={settings.taskItemsSortByDirection}
          disabledOptions={{
            [TaskItemsSortByOption.Owners]:
              settings.taskItemsGroupByOption === TeamTaskItemsGroupByOption.Owner ||
              settings.taskItemsGroupByOption === TeamTaskItemsGroupByOption.PrimaryOwner,
          }}
          onSortByChanged={handleSortByChanged}
          onSortDirectionChanged={handleSortDirectionChanged}
        />
      </Menu>
    </>
  );
};

export { TeamTaskItemsFilterButton };
